import { type Building, type HeatingNetwork, type Planning } from "@eisox/api/core-api";
import { Card, Circle, cn, Typography } from "@eisox/design-system";
import { isSummerModeActivated } from "@eisox/houses";
import { MULTISENSOR_ERRORS_ENUM, MULTISENSOR_WARNINGS_ENUM } from "@eisox/multisensors";
import { VALVE_ERRORS_ENUM, VALVE_WARNINGS_ENUM } from "@eisox/valves";

import { Help } from "~/components/help";
import { HybridTooltip } from "~/components/hybrid-tooltip";
import { FrostFreeSource, getRoomFrostFreeStatus } from "~/features/frost-free";
import { mergeTimeslot } from "~/features/planning";
import { m } from "~/paraglide/messages";
import type { EnhancedMultisensor } from "~/types/multisensor.type";
import type { EnhancedRoom } from "~/types/room.type";
import type { EnhancedValve } from "~/types/valve.type";

import {
  getCurrentPlanningType,
  getCurrentTimeslot,
  getRoomData,
  getSetpointTemp,
  getTemperature,
  roomDataIcon,
} from "../helpers";
import { SetpointTempTooltip } from "./setpoint-temp-tooltip";

interface SetpointTempProps {
  building: Building;
  room: EnhancedRoom;
  valves: EnhancedValve[];
  multisensors: EnhancedMultisensor[];
  heatingNetworks: HeatingNetwork[];
  plannings: Planning[];

  className?: string;
}

export const SetpointTemp: React.FC<SetpointTempProps> = ({
  building,
  room,
  valves,
  multisensors,
  heatingNetworks,
  plannings,
  className,
}) => {
  const { isAutoPrecomf, isSwitchEnabled, swOffset = 0 } = room;

  const connectedRoomValves = valves.filter(v =>
    [...v.warnings, ...v.errors].every(
      issue =>
        ![
          VALVE_WARNINGS_ENUM.DISCONNECTED_WARNING,
          VALVE_ERRORS_ENUM.DISCONNECTED,
          VALVE_ERRORS_ENUM.LONG_TIME_DISCONNECTED,
          VALVE_ERRORS_ENUM.DISCONNECTED_GATEWAY,
          VALVE_WARNINGS_ENUM.NOT_INSTALL,
          VALVE_WARNINGS_ENUM.NOT_ACTIVATED,
        ].includes(issue as VALVE_WARNINGS_ENUM | VALVE_ERRORS_ENUM),
    ),
  );

  const connectedMultisensors = multisensors.filter(ms =>
    [...ms.warnings, ...ms.errors].every(
      issue =>
        ![
          MULTISENSOR_WARNINGS_ENUM.DISCONNECTED_WARNING,
          MULTISENSOR_ERRORS_ENUM.DISCONNECTED,
          MULTISENSOR_ERRORS_ENUM.LONG_TIME_DISCONNECTED,
          MULTISENSOR_ERRORS_ENUM.DISCONNECTED_GATEWAY,
          MULTISENSOR_WARNINGS_ENUM.NOT_INSTALL,
          MULTISENSOR_WARNINGS_ENUM.NOT_ACTIVATED,
        ].includes(issue as MULTISENSOR_WARNINGS_ENUM | MULTISENSOR_ERRORS_ENUM),
    ),
  );

  const { anticipation, autoConf, autoPreConf, openWin, sun } = getRoomData({
    anticipationStart: room.anticipationStart,
    autoConfStart: room.autoConfStart,
    openWinStart: room.openWinStart,
    autoPreConfStart: room.autoPreConfStart,
    valves: connectedRoomValves.map(v => ({ temperature: v.sensors?.temperature, light: v.sensors?.light })),
    isAutoPrecomf: room.isAutoPrecomf,
  });

  const isSummerMode = isSummerModeActivated(building.summerModePeriod?.begin, building.summerModePeriod?.end);

  const allValvesDisconnected = connectedRoomValves.length === 0;
  const allMultisensorsDisconnected = connectedMultisensors.length === 0;

  const messageKey =
    allValvesDisconnected && allMultisensorsDisconnected
      ? m.setpoint_temp_disconnected()
      : isSummerMode
        ? m.setpoint_temp_summer_mode()
        : openWin.active
          ? m.setpoint_temp_open_window()
          : null;

  if (messageKey) {
    return (
      <Card
        className={cn(
          `relative flex flex-1 flex-nowrap items-center justify-center gap-[10px] overflow-hidden text-base text-nowrap whitespace-nowrap`,
          className,
        )}
      >
        <Typography>{messageKey}</Typography>
      </Card>
    );
  }

  const networksOfRoom = heatingNetworks.filter(network =>
    valves.some(valve => valve.roomId === room.id && valve.heatingNetworkId === network.id),
  );
  const { active: frostFreeActive, source: frostFreeSource } = getRoomFrostFreeStatus({
    buildingFrostFree: building.frostFree,
    roomFrostFree: room.frostFree,
    networksOfRoom,
  });
  // A partial network frost free leaves part of the room heated, so the setpoint still applies.
  const isFrostFree = frostFreeActive && frostFreeSource !== FrostFreeSource.HEATING_NETWORK_PARTIAL;

  const mergedPlannings = mergeTimeslot(plannings);
  const currentTimeSlot = getCurrentTimeslot(mergedPlannings);

  const autoPreConfEnabled = isAutoPrecomf && autoPreConf.active;

  const currentTimeSlotTypeForConsign = getCurrentPlanningType(
    autoConf.active,
    anticipation.active,
    autoPreConfEnabled ?? false,
    isFrostFree,
    currentTimeSlot.type,
    connectedRoomValves,
    building,
    room,
  );

  const swOffsetEnabled =
    !isFrostFree &&
    (currentTimeSlotTypeForConsign === "comfort" || currentTimeSlotTypeForConsign === "preComfort") &&
    !!isSwitchEnabled;

  const setpointTemp = getSetpointTemp(building, room, currentTimeSlotTypeForConsign, swOffsetEnabled, swOffset);

  const activeRoomDataIcons = Object.entries({ anticipation, autoConf, autoPreConf, openWin, sun }).filter(
    ([, value]) => value.active && !value.disabled,
  );

  return (
    <HybridTooltip
      className="max-w-[550px]!"
      content={
        currentTimeSlotTypeForConsign !== "frostFree" && (
          <SetpointTempTooltip
            currentTimeSlot={{
              temperature: getTemperature(building, room, currentTimeSlotTypeForConsign),
              type: currentTimeSlotTypeForConsign,
            }}
            swOffset={swOffset}
            roomData={{ anticipation, autoConf, autoPreConf, openWin, sun }}
            showSwOffset={swOffsetEnabled}
          />
        )
      }
    >
      <Card
        className={cn(
          "min-w-0 relative flex flex-1 flex-nowrap items-center justify-center gap-[8px] overflow-hidden text-base",
          className,
        )}
      >
        <p className="text-base leading-none whitespace-nowrap max-[420px]:text-[10px]">
          {`${m.setpoint()}: ${setpointTemp?.toFixed(1) ?? "-"}`}
        </p>

        <div className="flex shrink-0 items-center gap-[8px]">
          {activeRoomDataIcons.length > 0 && (
            <div className="max-[768px]:gap-0 flex items-center justify-center gap-[6px] max-[992px]:gap-[2px] [&>*:not(:first-child)]:ml-[-4px] max-[992px]:[&>*:not(:first-child)]:ml-[-8px] max-[768px]:[&>*:not(:first-child)]:ml-[-12px] max-[420px]:[&>*:not(:first-child)]:ml-[-14px]">
              {activeRoomDataIcons.map(([key], index) => (
                <div
                  key={`${key}-${index}`}
                  className="relative z-0 rounded-full ring-2 ring-primary-white max-[420px]:scale-90"
                >
                  <Circle.Root size={30} backgroundColor="blue" color="white">
                    {roomDataIcon[key as keyof typeof roomDataIcon]}
                  </Circle.Root>
                </div>
              ))}
            </div>
          )}
          <div className="shrink-0">
            <Help />
          </div>
        </div>
      </Card>
    </HybridTooltip>
  );
};

SetpointTemp.displayName = "SetpointTemp";
